/**
 * JWT Decoder/Encoder View
 * Interactive JWT decoding and encoding
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

type Mode = 'decode' | 'encode';

export const JwtView: React.FC = () => {
    const [mode, setMode] = useState<Mode>('decode');
    const [token, setToken] = useState('');
    const [payload, setPayload] = useState('');
    const [secret, setSecret] = useState('');
    const [result, setResult] = useState<{ header?: object; payload?: object; signature?: string; token?: string }>({});
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<'mode' | 'token' | 'payload' | 'secret' | 'process'>('mode');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    useInput((input, key) => {
        if (key.tab) {
            const fields: typeof focusedField[] = mode === 'decode'
                ? ['mode', 'token', 'process']
                : ['mode', 'payload', 'secret', 'process'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.return) {
            if (focusedField === 'mode') {
                setMode(mode === 'decode' ? 'encode' : 'decode');
                setResult({});
                setError('');
            } else {
                // Process on Enter from any input field or process button
                handleProcess();
            }
        } else if (input === 'c' && result.token) {
            handleCopy();
        }
    });

    const handleProcess = () => {
        try {
            setError('');
            if (mode === 'decode') {
                if (!token) {
                    setError('Please enter a JWT token');
                    return;
                }
                const decoded = service.jwtDecode(token);
                setResult(decoded);
            } else {
                if (!payload) {
                    setError('Please enter payload JSON');
                    return;
                }
                if (!secret) {
                    setError('Please enter a secret key');
                    return;
                }
                const payloadObj = JSON.parse(payload);
                const encoded = service.jwtEncode(payloadObj, secret);
                setResult({ token: encoded });
            }
            setCopied(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Processing failed');
            setResult({});
        }
    };

    const handleCopy = async () => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(result.token || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🔑 JWT Decoder/Encoder
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'mode' ? 'green' : undefined}>
                            {focusedField === 'mode' ? '▶ ' : '  '}Mode:
                        </Text>
                    </Box>
                    <Text bold color={focusedField === 'mode' ? 'yellow' : undefined}>
                        {mode === 'decode' ? 'DECODE' : 'ENCODE'}
                    </Text>
                    {focusedField === 'mode' && (
                        <Text dimColor> (Enter to toggle)</Text>
                    )}
                </Box>

                {mode === 'decode' ? (
                    <Box marginBottom={1} flexDirection="column">
                        <Box marginBottom={0}>
                            <Text color={focusedField === 'token' ? 'green' : undefined}>
                                {focusedField === 'token' ? '▶ ' : '  '}JWT Token:
                            </Text>
                        </Box>
                        <Box marginLeft={2} width={60}>
                            {focusedField === 'token' ? (
                                <TextInput
                                    value={token}
                                    onChange={setToken}
                                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                                />
                            ) : (
                                <Text dimColor={!token}>{token || '(empty)'}</Text>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Box marginBottom={1} flexDirection="column">
                            <Box marginBottom={0}>
                                <Text color={focusedField === 'payload' ? 'green' : undefined}>
                                    {focusedField === 'payload' ? '▶ ' : '  '}Payload (JSON):
                                </Text>
                            </Box>
                            <Box marginLeft={2} width={60}>
                                {focusedField === 'payload' ? (
                                    <TextInput
                                        value={payload}
                                        onChange={setPayload}
                                        placeholder='{"sub":"123","name":"John"}'
                                    />
                                ) : (
                                    <Text dimColor={!payload}>{payload || '(empty)'}</Text>
                                )}
                            </Box>
                        </Box>

                        <Box marginBottom={1} flexDirection="column">
                            <Box marginBottom={0}>
                                <Text color={focusedField === 'secret' ? 'green' : undefined}>
                                    {focusedField === 'secret' ? '▶ ' : '  '}Secret Key:
                                </Text>
                            </Box>
                            <Box marginLeft={2} width={60}>
                                {focusedField === 'secret' ? (
                                    <TextInput
                                        value={secret}
                                        onChange={setSecret}
                                        placeholder="your-secret-key"
                                        mask="*"
                                    />
                                ) : (
                                    <Text dimColor={!secret}>
                                        {secret ? '*'.repeat(secret.length) : '(empty)'}
                                    </Text>
                                )}
                            </Box>
                        </Box>
                    </>
                )}

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'process' ? 'green' : undefined}
                        color={focusedField === 'process' ? 'black' : 'green'}
                    >
                        {focusedField === 'process' ? '▶ ' : '  '}
                        [ {mode === 'decode' ? 'Decode' : 'Encode'} ]
                    </Text>
                </Box>
            </Box>

            {error && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="red"
                    paddingX={2}
                    paddingY={1}
                    marginBottom={1}
                >
                    <Text color="red">✗ Error: {error}</Text>
                </Box>
            )}

            {(result.header || result.token) && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Result:
                        </Text>
                        {copied && result.token && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        {result.header && (
                            <>
                                <Text bold color="yellow">Header:</Text>
                                <Text>{JSON.stringify(result.header, null, 2)}</Text>
                                <Text> </Text>
                                <Text bold color="yellow">Payload:</Text>
                                <Text>{JSON.stringify(result.payload, null, 2)}</Text>
                                <Text> </Text>
                                <Text bold color="yellow">Signature:</Text>
                                <Text dimColor>{result.signature}</Text>
                            </>
                        )}
                        {result.token && (
                            <>
                                <Text bold color="yellow">Token:</Text>
                                <Text wrap="wrap">{result.token}</Text>
                            </>
                        )}
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Toggle/{mode === 'decode' ? 'Decode' : 'Encode'} | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
